/**
 * IAuthAdapter Interface
 *
 * Defines the contract for authentication adapters used by AuthService.
 * Enables provider-agnostic authentication — swap Firebase/IDP for
 * Auth0, Cognito, or any other provider by writing a new adapter class
 * that implements this interface.
 *
 * Implementations:
 * - FirebaseAuthAdapter: Google Identity Platform / Firebase Auth
 *
 * @interface IAuthAdapter
 */

export class IAuthAdapter {
  /**
   * Initialise the adapter with provider config.
   *
   * @param {Object} config - Provider-specific configuration object
   * @returns {Promise<void>}
   */
  // eslint-disable-next-line no-unused-vars
  async initialize(config) {
    throw new Error('initialize() must be implemented by adapter');
  }

  /**
   * Sign in with email and password.
   *
   * @param {string} email
   * @param {string} password
   * @returns {Promise<Object>} Provider user object
   */
  // eslint-disable-next-line no-unused-vars
  async signInWithEmailAndPassword(email, password) {
    throw new Error('signInWithEmailAndPassword() must be implemented by adapter');
  }

  /**
   * Sign in with the SSO provider identified by id.
   * SAML providers use redirect (SDK-enforced).
   * All other providers try popup first, falling back to redirect.
   *
   * @param {string} providerId - Provider id from SSO_PROVIDERS config (e.g. "google", "microsoft.com", "saml.azure")
   * @returns {Promise<Object|null>} Provider user object on popup success; null when redirect is initiated
   */
  // eslint-disable-next-line no-unused-vars
  async signInWithProvider(providerId) {
    throw new Error('signInWithProvider() must be implemented by adapter');
  }

  /**
   * Send a passwordless sign-in link to the given email.
   *
   * @param {string} email
   * @param {Object} actionCodeSettings - { url, handleCodeInApp }
   * @returns {Promise<void>}
   */
  // eslint-disable-next-line no-unused-vars
  async sendSignInLinkToEmail(email, actionCodeSettings) {
    throw new Error('sendSignInLinkToEmail() must be implemented by adapter');
  }

  /**
   * Check whether the current URL is a sign-in link.
   *
   * @param {string} url
   * @returns {boolean}
   */
  // eslint-disable-next-line no-unused-vars
  isSignInWithEmailLink(url) {
    throw new Error('isSignInWithEmailLink() must be implemented by adapter');
  }

  /**
   * Complete email link sign-in.
   *
   * @param {string} email
   * @param {string} url - Current window location href
   * @returns {Promise<Object>} Provider user object
   */
  // eslint-disable-next-line no-unused-vars
  async signInWithEmailLink(email, url) {
    throw new Error('signInWithEmailLink() must be implemented by adapter');
  }

  /**
   * Send a password reset email via the backend.
   * Routes through the backend rather than calling the provider SDK directly.
   *
   * @param {string} email
   * @returns {Promise<void>}
   */
  // eslint-disable-next-line no-unused-vars
  async sendPasswordResetEmail(email) {
    throw new Error('sendPasswordResetEmail() must be implemented by adapter');
  }

  /**
   * Confirm a password reset using the oobCode from the reset link.
   * Routes through the backend — frontend stays provider-agnostic.
   *
   * @param {string} oobCode - Out-of-band code from the reset URL
   * @param {string} newPassword
   * @returns {Promise<void>}
   */
  // eslint-disable-next-line no-unused-vars
  async confirmPasswordReset(oobCode, newPassword) {
    throw new Error('confirmPasswordReset() must be implemented by adapter');
  }

  /**
   * Update the display name on the authenticated user's provider profile.
   *
   * @param {Object} user - Provider user object
   * @param {Object} profile - { displayName, photoURL }
   * @returns {Promise<void>}
   */
  // eslint-disable-next-line no-unused-vars
  async updateProfile(user, profile) {
    throw new Error('updateProfile() must be implemented by adapter');
  }

  /**
   * Get a fresh ID token for the authenticated user.
   *
   * @param {Object} user - Provider user object
   * @param {boolean} [forceRefresh=false]
   * @returns {Promise<string>} JWT token string
   */
  // eslint-disable-next-line no-unused-vars
  async getIdToken(user, forceRefresh = false) {
    throw new Error('getIdToken() must be implemented by adapter');
  }

  /**
   * Subscribe to auth state changes.
   *
   * @param {Function} callback - Called with (user | null) on each change
   * @returns {Function} Unsubscribe function
   */
  // eslint-disable-next-line no-unused-vars
  onAuthStateChanged(callback) {
    throw new Error('onAuthStateChanged() must be implemented by adapter');
  }

  /**
   * Sign the current user out.
   *
   * @returns {Promise<void>}
   */
  async signOut() {
    throw new Error('signOut() must be implemented by adapter');
  }

  /**
   * Return the full config for all configured SSO providers.
   * Used by the login page to render one button per enabled provider.
   *
   * @returns {Array<{ id: string, label: string, type: string }>}
   */
  getSsoProviders() {
    throw new Error('getSsoProviders() must be implemented by adapter');
  }
}
